import config, { routePaths } from 'appConfig';
import { Button, Container, Link } from 'components/common';
import useScroll from 'hooks/useScroll';
import { sendEvent } from 'lib/gtm';
import menu from 'lib/home/data/raw.json';
import notification from 'lib/notification';
import { useBuildNewOrder, useReorder } from 'lib/order';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { BuildNewOrderConfirmationModal, useHomePage } from '.';
import { COUPON_CODE_QUERY_PARAM } from 'lib/coupons';

const MealActionBar = () => {
  const router = useRouter();
  const {
    isAuthLoading,
    isOrdersLoading,
    user,
    totalOrders,
    lastCompletedOrder,
  } = useHomePage();

  const { onReorder, isReplicating } = useReorder();
  const {
    onBuildNewOrder,
    isLoading: isBuildingNewOrder,
    showBuildNewOrderModal,
    setShowBuildNewOrderModal,
    quantity,
  } = useBuildNewOrder();

  const isScrolled = useScroll(350);

  const { product_sections } = menu;
  const customizableMeal = product_sections[0];

  const handleReorder = useCallback(
    async (orderId?: string) => {
      if (!orderId) return;

      await onReorder({
        identifier: orderId,
        onError: (message) => {
          notification.error(message);
        },
        onWarning: (
          productNames,
          order,
          isSomeProductUnPurchasable,
        ) => {
          notification.warning(
            <div>
              {isSomeProductUnPurchasable
                ? 'Oops! These items were available in your previous order, but isn’t eligible for purchase under your account now. 😓 '
                : 'Oops! These items are out of stock and were not added to your cart: '}
              <br />
              <ul style={{ listStyle: 'inside' }}>
                {productNames.map((productName) => (
                  <li key={productName}>{productName}</li>
                ))}
              </ul>
              {!isSomeProductUnPurchasable &&
                'You can either checkout with the rest of the items or add more to your cart! 🥳'}
            </div>,
            null,
            { autoClose: 10000 },
          );

          sendEvent('reorder', {
            order_id: order.id,
            content_type: 'button',
            content_id: 'reorder_from_action_bar',
          });
        },
        onSuccess: (order, message) => {
          notification.success(message);

          sendEvent('reorder', {
            order_id: order.id,
            content_type: 'button',
            content_id: 'reorder_from_home_banner',
          });

          router.push(routePaths.CHECKOUT);
        },
      });
    },
    [onReorder, router],
  );

  const handleBuildNewOrder = useCallback(
    async (clearCart: boolean) => {
      setShowBuildNewOrderModal(false);
      await onBuildNewOrder(clearCart, {
        onSuccess: () => {
          router.push(routePaths.CATEGORIES);
        },
      });
    },
    [onBuildNewOrder, router, setShowBuildNewOrderModal],
  );

  if (isAuthLoading) return null;

  return (
    <>
      <div
        className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white md:tw-hidden tw-z-[1] tw-h-[64px] tw-transition-transform tw-duration-500 tw-flex tw-justify-center tw-items-center"
        style={{
          boxShadow: '0 -10px 20px rgba(0,0,0,0.15)',
          transform: isScrolled
            ? 'translateY(0)'
            : 'translateY(100%)',
        }}
      >
        <Container>
          {user ? (
            isOrdersLoading ? null : (
              <>
                {totalOrders > 0 ? (
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-4">
                    {lastCompletedOrder && (
                      <Button
                        className="tw-uppercase tw-min-w-0 tw-text-lg"
                        loading={isReplicating}
                        disabled={isReplicating}
                        onClick={async () =>
                          await handleReorder(lastCompletedOrder.id)
                        }
                      >
                        <>Repeat an Order</>
                      </Button>
                    )}
                    <Button
                      className="tw-uppercase tw-min-w-0 tw-text-lg tw-bg-secondary tw-border-secondary hover:tw-bg-secondary-600 tw-text-[#1d1d1d]"
                      color="secondary"
                      disabled={isBuildingNewOrder}
                      loading={isBuildingNewOrder}
                      onClick={async () => {
                        if (quantity > 0) {
                          setShowBuildNewOrderModal(true);
                        } else {
                          await handleBuildNewOrder(false);
                        }
                      }}
                    >
                      <>See Menu / Build New Order</>
                    </Button>
                  </div>
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                    <div className="tw-flex tw-flex-col tw-flex-1">
                      <p className="tw-mb-0 tw-leading-tight tw-text-[11px]">
                        Use code{' '}
                        <strong>{config.welcomeCouponCode}</strong> to{' '}
                        score <strong>$10 OFF + FREE Delivery</strong>
                        ! (Min. cart: $50)
                      </p>
                    </div>
                    <Link
                      to={`${routePaths.CATEGORIES}?${COUPON_CODE_QUERY_PARAM}=${config.welcomeCouponCode}`}
                    >
                      <Button className="tw-uppercase tw-min-w-0 tw-text-lg tw-whitespace-nowrap">
                        <>Order Now</>
                      </Button>
                    </Link>
                  </div>
                )}
              </>
            )
          ) : (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-4">
              <Link
                to={`${routePaths.PRODUCTS}?category_ids=${customizableMeal.query?.category_ids?.[0]}`}
              >
                <Button className="tw-uppercase tw-min-w-0 tw-text-lg">
                  <>Customise Your Meal</>
                </Button>
              </Link>
              <Link to={routePaths.CATEGORIES}>
                <Button
                  className="tw-uppercase tw-min-w-0 tw-text-lg tw-text-[#1d1d1d] tw-border-secondary hover:tw-bg-secondary-400"
                  variant="contained"
                  color="secondary"
                >
                  <>Explore menu</>
                </Button>
              </Link>
            </div>
          )}
        </Container>
      </div>
      <BuildNewOrderConfirmationModal
        isOpen={showBuildNewOrderModal}
        onClose={() => setShowBuildNewOrderModal(false)}
        onClear={async () => await handleBuildNewOrder(true)}
        onKeep={async () => await handleBuildNewOrder(false)}
      />
    </>
  );
};

export default MealActionBar;
